import { useEffect, useRef, useState, RefObject, useCallback } from 'react';
import { FaceMesh, NormalizedLandmarkList } from '@mediapipe/face_mesh';
import { LogEvents } from '../../models/InterviewOnboardingModel';
import { assessmentLog } from '../../services/api/assessment';
import { tokenKey } from '../../utils/apiClient/contant';
import { baseURL } from '../../utils/apiClient';
import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';
import {
  setCurrentMinute,
  setShouldSubmitAssessment,
  setTotalDeviationForMinute,
} from '../../store/proctoringProvider/proctoringProviderSlice';

type GazeEvent = 'OUT_OF_VIEW' | 'BACK_IN_VIEW' | 'EYE_VIEW_DEVIATION' | null;
interface DeviationRecord {
  minute: number;
  totalDeviation: number; // in milliseconds ✅
}

interface UseProctoringOptions {
  accessKey: string;
  videoRef: RefObject<HTMLVideoElement>;
}

export function useMediapipeProctoring({
  accessKey,
  videoRef,
}: UseProctoringOptions) {
  const [faceMeshLoaded, setFaceMeshLoaded] = useState(false);
  const [isOutOfView, setIsOutOfView] = useState(false);
  const previousLogEvent = useRef<GazeEvent>(LogEvents.OUT_OF_VIEW);
  const [logEvent, setLogEvent] = useState<{
    event: GazeEvent;
    time: string | null;
  }>({
    event: null,
    time: null,
  });
  const [deviationHistory, setDeviationHistory] = useState<
    {
      minute: number;
      totalDeviation: number;
    }[]
  >([]);
  const deviationStartRef = useRef<number | null>(null);
  const currentMinuteRef = useRef<number>(1);
  const deviationHistoryRef = useRef<DeviationRecord[]>([]);
  const token = sessionStorage.getItem(tokenKey) ?? '';
  let firstResultLogged = false; // 👈 Add this outside of useEffect, top-level in the hook
  const dispatch = useAppDispatch();
  const { totalDeviationForMinute, shouldSubmitLog } = useAppSelector(
    (state) => state.proctoring
  );
  const currentMinute = useAppSelector(
    (state) => state.proctoring.currentMinute
  );
  const lockForLog = useRef<boolean>(false);

  useEffect(() => {
    deviationHistoryRef.current = deviationHistory;
  }, [deviationHistory]);

  useEffect(() => {
    const tm = setInterval(() => {}, 5_000);
    return () => {
      clearInterval(tm);
    };
  }, []);

  // Track minute index
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const currentMinute = currentMinuteRef.current;

      // If deviation ongoing, flush partial duration for current minute
      if (deviationStartRef.current !== null) {
        const durationMs = now - deviationStartRef.current;

        setDeviationHistory((prev) => {
          const existing = prev.find((d) => d.minute === currentMinute);
          if (existing) {
            return prev.map((d) =>
              d.minute === currentMinute
                ? { ...d, totalDeviation: d.totalDeviation + durationMs }
                : d
            );
          } else {
            return [
              ...prev,
              { minute: currentMinute, totalDeviation: durationMs },
            ];
          }
        });

        deviationStartRef.current = now; // reset timer for next minute
      }

      // Get total deviation for the finished minute to send in log
      const totalDeviationForMinute =
        deviationHistoryRef.current.find((d) => d.minute === currentMinute)
          ?.totalDeviation ?? 0;
      // if (canProceed) {
      //   try {

      callAssessmentLog(totalDeviationForMinute);
      // assessmentLog(accessKey, LogEvents.EYE_VIEW_DEVIATION, {
      //   deviation_time: totalDeviationForMinute,
      // });
      //   } catch (error) {
      //   } finally {
      //     releaseLogLock();
      //   }
      // }

      dispatch(
        setTotalDeviationForMinute({
          event_name: LogEvents.EYE_VIEW_DEVIATION,
          totalDeviationForMinute: 0,
        })
      );
      currentMinuteRef.current += 1;
    }, 60000); // every 60 seconds

    const handleBeforeUnload = () => {
      const currentMinute = currentMinuteRef.current;
      const deviationForCurrentMinute =
        deviationHistoryRef.current.find((d) => d.minute === currentMinute)
          ?.totalDeviation ?? 0;

      localStorage.setItem(
        'deviation_time_pending',
        JSON.stringify({
          storedDeviation: deviationForCurrentMinute,
          storedAccessKey: accessKey,
        })
      );
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);
    dispatch(setCurrentMinute(currentMinuteRef.current));
    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
    };
  }, [accessKey]);

  useEffect(() => {
    if (!accessKey || !shouldSubmitLog) return;
    const totalDeviationForMinute =
      deviationHistoryRef.current.find(
        (d) => d.minute === currentMinuteRef.current
      )?.totalDeviation ?? 0;

    callAssessmentLog(totalDeviationForMinute).finally(() => {
      dispatch(setShouldSubmitAssessment(true));
    });
  }, [accessKey, shouldSubmitLog]);

  async function callAssessmentLog(totalDeviationForMinute: number) {
    if (lockForLog.current) return Promise.resolve(); // Prevent multiple calls
    lockForLog.current = true;

    const totalDeviationForMinute1 =
      deviationHistoryRef.current.find(
        (d) => d.minute === currentMinuteRef.current
      )?.totalDeviation ?? 0;

    assessmentLog(accessKey, LogEvents.EYE_VIEW_DEVIATION, {
      deviation_time: totalDeviationForMinute,
    })
      .catch((error) => {
        console.error('❌ Error sending assessment log:', error);
      })
      .finally(() => {
        lockForLog.current = false; // Release lock after log is sent
      });
  }

  useEffect(() => {
    const interval = setInterval(() => {
      if (
        deviationHistoryRef.current &&
        deviationHistoryRef?.current?.length > 0
      ) {
        const totalDeviationForMinute =
          deviationHistoryRef.current[deviationHistoryRef.current.length - 1]
            .totalDeviation;
        dispatch(setCurrentMinute(currentMinuteRef.current));
        dispatch(
          setTotalDeviationForMinute({
            event_name: LogEvents.EYE_VIEW_DEVIATION,
            totalDeviationForMinute: totalDeviationForMinute,
          })
        );
      }
    }, 1000); // ⏱️ Every 1 seconds

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('deviation_time_pending');
    if (!stored) return;
    try {
      const { storedDeviation, storedAccessKey } = JSON.parse(stored);
      if (storedAccessKey === accessKey) {
        assessmentLog(accessKey, LogEvents.EYE_VIEW_DEVIATION, {
          deviation_time: storedDeviation,
        });
      }
    } catch (error) {
      console.error('❌ Error parsing or sending stored deviation:', error);
    } finally {
      localStorage.removeItem('deviation_time_pending');
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const faceMesh = new FaceMesh({
      locateFile: (file) =>
        `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
    });

    faceMesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });

    faceMesh.onResults(onResults);

    let animationFrameId: number;

    const processFrame = async () => {
      if (video.readyState === 4) {
        await faceMesh.send({ image: video });
      }
      animationFrameId = requestAnimationFrame(processFrame);
    };

    animationFrameId = requestAnimationFrame(processFrame);

    function detectGazeDirection(
      iris: any,
      left: any,
      right: any,
      top: any,
      bottom: any
    ): string {
      const eyeWidth = right.x - left.x;
      const eyeHeight = bottom.y - top.y;

      const irisX = (iris.x - left.x) / eyeWidth;
      const irisY = (iris.y - top.y) / eyeHeight;

      if (irisX < 0.3) return 'LEFT';
      if (irisX > 0.7) return 'RIGHT';
      if (irisY < 0.3) return 'UP';
      if (irisY > 0.7) return 'DOWN';
      return 'CENTER';
    }

    function onResults(results: {
      multiFaceLandmarks: NormalizedLandmarkList[];
    }) {
      if (!faceMeshLoaded) {
        setFaceMeshLoaded(true); // ✅ Mark model as loaded
      }
      let event: GazeEvent;

      if (results.multiFaceLandmarks.length > 0) {
        const landmarks = results.multiFaceLandmarks[0];
        const iris = landmarks[468];
        const left = landmarks[33];
        const right = landmarks[133];
        const top = landmarks[159];
        const bottom = landmarks[145];

        const direction = detectGazeDirection(iris, left, right, top, bottom);
        event = ['LEFT', 'RIGHT', 'UP', 'DOWN'].includes(direction)
          ? LogEvents.EYE_VIEW_DEVIATION
          : LogEvents.BACK_IN_VIEW;
      } else {
        event = LogEvents.OUT_OF_VIEW;
      }

      setLogEvent({ event, time: new Date().toLocaleString() });

      // 🔥 Force first log only once on initial result
      if (!firstResultLogged) {
        previousLogEvent.current = null; // force log even if event hasn't changed
        firstResultLogged = true;
      }
    }

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [videoRef.current]); // only restart when the actual DOM element changes

  useEffect(() => {
    if (faceMeshLoaded) {
      assessmentLog(accessKey, LogEvents.MESH_INITIATED);
    }
  }, [faceMeshLoaded]);

  const handleDeviationTracking = (event: GazeEvent) => {
    const now = Date.now();

    if (event === LogEvents.EYE_VIEW_DEVIATION) {
      if (deviationStartRef.current === null) {
        deviationStartRef.current = now;
      }
    } else {
      if (deviationStartRef.current !== null) {
        const durationMs = now - deviationStartRef.current;
        const currentMinute = currentMinuteRef.current;

        setDeviationHistory((prev) => {
          const existing = prev.find((d) => d.minute === currentMinute);
          if (existing) {
            return prev.map((d) =>
              d.minute === currentMinute
                ? {
                    ...d,
                    totalDeviation: d.totalDeviation + durationMs,
                  }
                : d
            );
          } else {
            return [
              ...prev,
              {
                minute: currentMinute,
                totalDeviation: durationMs,
              },
            ];
          }
        });

        deviationStartRef.current = null;
      }
    }
  };

  const throttledLog = useThrottledCallback(() => {
    if (previousLogEvent.current === logEvent.event) return;

    previousLogEvent.current = logEvent.event;

    if (isOutOfView && logEvent.event === LogEvents.BACK_IN_VIEW) {
      setIsOutOfView(false);
      assessmentLog(accessKey, LogEvents.BACK_IN_VIEW);
      return;
    }

    if (logEvent.event === LogEvents.OUT_OF_VIEW) {
      setIsOutOfView(true);
      assessmentLog(accessKey, LogEvents.OUT_OF_VIEW);
    } else if (logEvent.event === LogEvents.BACK_IN_VIEW) {
      // assessmentLog(accessKey, LogEvents.EYE_VIEW_RESTORED);
    } else if (logEvent.event === LogEvents.EYE_VIEW_DEVIATION) {
      // assessmentLog(accessKey, LogEvents.EYE_VIEW_DEVIATION);
    }
    handleDeviationTracking(logEvent.event);
  }, 500);

  useEffect(() => {
    if (logEvent.event && logEvent.time) {
      throttledLog();
    }
  }, [logEvent]);

  return { isOutOfView, logEvent };
}

// Utility hook for throttling
function useThrottledCallback(callback: () => void, delay: number) {
  const lastCallRef = useRef<number>(0);

  return () => {
    const now = Date.now();
    if (now - lastCallRef.current >= delay) {
      lastCallRef.current = now;
      callback();
    }
  };
}
